import React, { useMemo, useState, useEffect } from "react";
import {
  Form,
  InputNumber,
  Select,
  Button,
  TimePicker,
  Card,
  Space,
  Tag,
  message,
} from "antd";
import moment from "moment";

import {
  GetSchoolNames,
  GetCMSDoc,
  UpdateCMSDoc,
} from "../../../../APIManager";

export default function BookingRulesEditor() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schools, setSchools] = useState([]);
  const [rules, setRules] = useState({});

  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resSchoolNames, resBoatBookingRules] = await Promise.all([
        GetSchoolNames(),
        GetCMSDoc("BoatBookingRules"),
      ]);
      setSchools(resSchoolNames.data);
      setRules(resBoatBookingRules.data.Data || {});
    } catch (err) {
      console.error(err);
      message.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (rules && Object.keys(rules).length) {
      form.setFieldsValue({
        SlotExemptions: rules.SlotExemptions || [],
        AdvanceBookingDays: rules.AdvanceBookingDays || 0,
        BookingOpenTime: rules.BookingOpenTime
          ? moment(rules.BookingOpenTime, "HH:mm")
          : null,
        ExtraBookingOpenTime: rules.ExtraBookingOpenTime
          ? moment(rules.ExtraBookingOpenTime, "HH:mm")
          : null,
        BookingCloseTime: rules.BookingCloseTime
          ? moment(rules.BookingCloseTime, "HH:mm")
          : null,
      });
    }
  }, [rules, form]);

  const schoolOptions = useMemo(() => {
    const sorted = [...schools].sort((a, b) =>
      a.username.localeCompare(b.username)
    );
    return sorted.map((s) => ({
      label: s.username,
      value: s._id,
    }));
  }, [schools]);

  const handleSave = async (vals) => {
    const data = {
      SlotExemptions: vals.SlotExemptions,
      AdvanceBookingDays: vals.AdvanceBookingDays,
      BookingOpenTime: vals.BookingOpenTime.format("HH:mm"),
      ExtraBookingOpenTime: vals.ExtraBookingOpenTime.format("HH:mm"),
      BookingCloseTime: vals.BookingCloseTime.format("HH:mm"),
    };

    setSaving(true);
    try {
      await UpdateCMSDoc({
        docname: "BoatBookingRules",
        data,
      });
      message.success("Booking rules saved successfully");
      setRules(data); // update local state
    } catch (err) {
      console.error(err);
      message.error("Failed to save booking rules");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card title="Booking Rules" className="p-4 rounded-2xl shadow-sm">
        Loading...
      </Card>
    );
  }

  return (
    <Card title="Booking Rules" className="p-4 rounded-2xl shadow-sm">
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Form.Item
          label="Advance Booking Days"
          name="AdvanceBookingDays"
          rules={[
            { required: true, message: "Please enter advance booking days" },
            { type: "number", min: 0, message: "Value cannot be negative" },
          ]}
        >
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          label="Booking Open Time"
          name="BookingOpenTime"
          rules={[
            { required: true, message: "Please select booking open time" },
          ]}
        >
          <TimePicker format="HH:mm" style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          label="Extra Booking Open Time"
          name="ExtraBookingOpenTime"
          rules={[
            {
              required: true,
              message: "Please select extra booking open time",
            },
          ]}
        >
          <TimePicker format="HH:mm" style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          label="Booking Close Time"
          name="BookingCloseTime"
          rules={[
            {
              required: true,
              message: "Please select booking close time",
            },
          ]}
        >
          <TimePicker format="HH:mm" style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="Slot Exemptions (Schools)" name="SlotExemptions">
          <Select
            mode="multiple"
            showSearch
            placeholder="Select schools"
            options={schoolOptions}
            allowClear
            filterOption={(input, option) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
            tagRender={({ label, closable, onClose }) => (
              <Tag
                closable={closable}
                onClose={onClose}
                className="rounded-xl px-2 py-1"
              >
                {label}
              </Tag>
            )}
          />
        </Form.Item>

        <Space style={{ marginTop: 16 }}>
          <Button type="primary" htmlType="submit" loading={saving}>
            Save Rules
          </Button>
        </Space>
      </Form>
    </Card>
  );
}
